import React, { FC } from 'react';

import { EditableComponent } from '@adobe/aem-react-editable-components';
import { useQuery } from '@apollo/client';
import { phoenixLegalMemberTierQuery } from '@marriott/mi-legal-graphql';
import { v4 as uuidv4 } from 'uuid';
import { RichText as RichTextComp, TrackingPropsCarousel, VanilaCardCarouselContainer } from '@marriott/mi-ui-library';
import { useAuthStore, useLegalGenericStore } from '../../store';
import { generateApolloClientHeadersForUXLCalls } from '../../utils/uxlHelper';
import { LoyaltyDetailResponse, LoyaltyTierProps, Tier } from './LoyaltyTier.types';
import { StyledLoyaltyTier } from './LoyaltyTier.styles';
import { LoyaltyLoader } from './loyaltyTier.loading';

export const LoyaltyTierConfig = {
  emptyLabel: 'LoyaltyTier',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/membertier`,
};

const GET_MEMBERS_OPERATION_NAME = 'phoenixLegalMemberTierQuery';

// Use named rather than default exports.
export const LoyaltyTier: FC<LoyaltyTierProps> = props => {
  return (
    <EditableComponent config={LoyaltyTierConfig} {...props}>
      <LoyaltyWrapperComp {...props} />
    </EditableComponent>
  );
};

const TIERS = ['ambassador', 'gold', 'platinum', 'member', 'silver', 'titanium'] as const;
const TIER_BY_LOYALTY_CODE = {
  PFS: 'ambassador',
  PAC: 'platinum',
  PPC: 'titanium',
  GAC: 'gold',
  SAC: 'silver',
  MRD: 'member',
} as const;

const DEFAULT_TIER = 'member';

const LoyaltyWrapperComp: FC<LoyaltyTierProps> = props => {
  const { properties, isAuthorMode } = props;
  const { sessionId, isAuthenticated } = useAuthStore();
  const { language, sessionData } = useLegalGenericStore();

  const { data, loading } = useQuery<LoyaltyDetailResponse>(phoenixLegalMemberTierQuery, {
    variables: { customerId: sessionData?.cacheData?.data?.consumerID },
    context: generateApolloClientHeadersForUXLCalls(sessionId, GET_MEMBERS_OPERATION_NAME, language),
    onError(error) {
      console.error(`Something went wrong while fetching member details: ${JSON.stringify(error)}`);
    },
    skip: !isAuthenticated || isAuthorMode,
  });

  const code = data?.customer.loyaltyInformation.rewards.levelType.code as keyof typeof TIER_BY_LOYALTY_CODE;
  const tier = TIER_BY_LOYALTY_CODE[code] || DEFAULT_TIER;

  if (loading) return <LoyaltyLoader />;

  return (
    <StyledLoyaltyTier data-testid="legal-LoyaltyTier" data-component-name="o-legal-LoyaltyTier" data-tier={tier}>
      {isAuthorMode ? (
        TIERS.map(t => (
          <Carousel
            items={props[t] || []}
            noOfCards={properties?.noOfCards}
            noOfCardsTablet={properties?.noOfCardsTablet}
            trackingProperties={props.trackingProperties}
            tier={t}
            key={t}
          />
        ))
      ) : (
        <Carousel
          items={props[tier] || []}
          noOfCards={properties?.noOfCards}
          noOfCardsTablet={properties?.noOfCardsTablet}
          trackingProperties={props.trackingProperties}
        />
      )}
    </StyledLoyaltyTier>
  );
};

interface ICarousel {
  items: {
    data: string;
  }[];
  noOfCards: number;
  noOfCardsTablet: number;
  trackingProperties: TrackingPropsCarousel;
  tier?: Tier;
}
function Carousel({ items, trackingProperties, tier, noOfCardsTablet = 2, noOfCards = 3 }: ICarousel) {
  const id = uuidv4();

  return (
    <VanilaCardCarouselContainer
      componentId={`legal-Loyalty-member-tier-${id}`}
      variation={'standard'}
      cardCount={items?.length || 0}
      isCombo={() => false}
      noOfCardsTablet={noOfCardsTablet}
      noOfCards={noOfCards}
      trackingProperties={trackingProperties}
      eyebrow={tier}
      styleclass="py-3 px-0 mt-5 legal-box-shadow t-background-color legal-loyalty-call-us"
    >
      {items?.map((item: { data: string }, index: React.Key) => (
        <li key={`${id}-${index}`}>
          <RichTextComp text={item.data} componentId={`richtext-${id}-${index}`} />
        </li>
      ))}
    </VanilaCardCarouselContainer>
  );
}
